import React, { useEffect } from "react";
import InputText from "../InputText";
import LicensingTerm from "../Content/LicensingTerm";
import { useFormik } from "formik";
import Selector from "../Selector";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useState } from "react";
import * as yup from "yup";
import { createAgreement, updateAgreement } from "../../graphql/mutations";
import { generateClient } from "aws-amplify/api";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
	handleComplete,
	handleNext,
} from "../../redux/slices/addNewContentStepsSlice";
import {
	resetAgreementForm,
	setAgreementDetails,
} from "../../redux/slices/agreementFormSlice";
import { uploadData } from "aws-amplify/storage";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { EXCHANGE_RATE_URL } from "../../constants";
import axios from "axios";
import Grid from "@mui/material/Grid";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { v4 as uuidv4 } from "uuid";

import { Button } from "@mui/material";
import { useRef } from "react";
import { Delete, DeleteOutlineOutlined } from "@mui/icons-material";
import CommercialTermsOfAgreement from "./CommercialTermsOfAgreement";
import PhoneNumberInput from "../PhoneNumberInput";
import { validatePhoneNumber } from "../../utils/phoneValidation";
import ReadOnlyInput from "../ReadOnlyInput";

const stepOneValidationSchema = yup.object().shape({
	carrierType: yup
		.array()
		.of(yup.string())
		.min(1, "Please select at least a carrier type")
		.required("Please select at least a carrier type"),
	currency: yup
		.string()
		.min(3, "Currency code must be at least 3 characters")
		.max(3, "Currency code must be exactly 3 characters")
		.required("Currency is required!"),

	amountPaid: yup
		.number()
		.test(
			"is-required",
			"Amount of payment is required!",
			function (value) {
				const { paymentStatus } = this.parent;
				return (
					(paymentStatus !== "Paid" &&
						paymentStatus !== "PartiallyPaid") ||
					!!value
				);
			}
		)
		.typeError("Amount paid must be a number")
		.positive("Amount paid must be a positive number"),

	amountRemaining: yup
		.number()
		.nullable()
		.test(
			"is-required",
			"Amount of payment is required!",
			function (value) {
				const { paymentStatus } = this.parent;
				return paymentStatus !== "PartiallyPaid" || value !== null;
			}
		)
		.typeError("Amount remaining must be a number or null"),
	paymentModel: yup
		.string()
		.max(50, "Payment model cannot exceed 50 characters")
		.required("Payment model is required"),
	paymentPlan: yup
		.string()
		.max(50, "Payment plan cannot exceed 50 characters")
		.required("Payment plan is required"),
	paymentTerm: yup
		.string()
		.max(50, "Payment term cannot exceed 50 characters")
		.required("Payment term is required"),
	paymentStatus: yup
		.string()
		.max(20, "Payment status cannot exceed 20 characters")
		.required("Payment status is required"),
	paymentReceiptFilePath: yup
		.string()
		.test("is-required", "Payment receipt is required!", function (value) {
			const { paymentStatus } = this.parent;
			return (
				(paymentStatus !== "Paid" &&
					paymentStatus !== "PartiallyPaid") ||
				!!value
			);
		}),

	partyA: yup
		.number()
		.nullable()
		.when("paymentModel", {
			is: (paymentModel) => paymentModel === "RevenueShare",
			then: () =>
				yup
					.number()
					.required(
						"Party A is required when payment model is Revenue Share"
					)
					.typeError("Party A must be a number")
					.max(100, "Party A must not be greater than 100"),
			otherwise: () => yup.number().nullable(),
		}),

	partyB: yup
		.number()
		.nullable()
		.when("paymentModel", {
			is: (paymentModel) => paymentModel === "RevenueShare",
			then: () =>
				yup
					.number()
					.required(
						"Party B is required when payment model is Revenue Share"
					)
					.typeError("Party B must be a number")
					.max(100, "Party A must not be greater than 100"),
			otherwise: () => yup.number().nullable(),
		}),
	partySum: yup.number().when(["partyA", "partyB", "paymentModel"], {
		is: (partyA, partyB, paymentModel) =>
			paymentModel === "RevenueShare" && (partyA || partyB),
		then: () =>
			yup
				.mixed()
				.test(
					"party-sum",
					"The sum of Party A and Party B must be 100",
					function () {
						const { partyA, partyB } = this.parent;
						return (partyA || 0) + (partyB || 0) === 100;
					}
				),
	}),
});

const defaultValidationSchema = yup.object().shape({
	daysOfAgreement: yup.number().required("Term of Agreement is required"),
	dateOfAgreement: yup.string().required("Date Of Agreement is required!"),
	companyName: yup
		.string()
		.min(2, "Company name must be at least 2 characters")
		.max(100, "Company name cannot exceed 100 characters")
		.required("Name of Supplier is required!"),
	officialAddress: yup
		.string()
		.min(10, "Address must be at least 10 characters")
		.max(200, "Address cannot exceed 200 characters")
		.required("Official Address is required!"),
	emailAddress: yup
		.string()
		.email()
		.max(100, "Email cannot exceed 100 characters")
		.required("Email is required!"),
	signingAuthorityName: yup
		.string()
		.min(2, "Name must be at least 2 characters")
		.max(100, "Name cannot exceed 100 characters")
		.required("Signing Authority Name is required!"),
	designation: yup
		.string()
		.min(2, "Designation must be at least 2 characters")
		.max(50, "Designation cannot exceed 50 characters")
		.required("Designation is required!"),
	technicalName: yup
		.string()
		.min(2, "Name must be at least 2 characters")
		.max(100, "Name cannot exceed 100 characters")
		.required("Technical Name is required!"),
	technicalDesignation: yup
		.string()
		.min(2, "Designation must be at least 2 characters")
		.max(50, "Designation cannot exceed 50 characters")
		.required("Technical designation is required!"),
	technicalEmail: yup
		.string()
		.email()
		.max(100, "Email cannot exceed 100 characters")
		.required("Technical email is required!"),
	technicalPhoneNumber: yup
		.string()
		.required("Technical phone number is required")
		.test("phone", "Invalid phone number", function (value) {
			return validatePhoneNumber(value);
		}),
	billingName: yup
		.string()
		.min(2, "Name must be at least 2 characters")
		.max(100, "Name cannot exceed 100 characters")
		.required("Billing Name is required!"),
	billingDesignation: yup
		.string()
		.min(2, "Designation must be at least 2 characters")
		.max(50, "Designation cannot exceed 50 characters")
		.required("Billing Designation is required!"),
	billingEmail: yup
		.string()
		.email()
		.max(100, "Email cannot exceed 100 characters")
		.required("Billing Email is required!"),
	billingPhoneNumber: yup
		.string()
		.required("Billing phone number is required")
		.test("phone", "Invalid phone number", function (value) {
			return validatePhoneNumber(value);
		}),
	commercialName: yup
		.string()
		.min(2, "Name must be at least 2 characters")
		.max(100, "Name cannot exceed 100 characters")
		.required("Commercial Name is required!"),
	commercialDesignation: yup
		.string()
		.min(2, "Designation must be at least 2 characters")
		.max(50, "Designation cannot exceed 50 characters")
		.required("Commercial Designation is required!"),
	commercialEmail: yup
		.string()
		.email()
		.max(100, "Email cannot exceed 100 characters")
		.required("Commercial Email is required!"),
	commercialPhoneNumber: yup
		.string()
		.required("Commercial phone number is required")
		.test("phone", "Invalid phone number", function (value) {
			return validatePhoneNumber(value);
		}),
	agreementFilePath: yup.string().required("Agreement document is required"),
	numberOfRefreshCycles: yup
		.number()
		.typeError("Number of refresh cycles must be a number")
		.positive("Number of refresh cycles must be a positive number")
		.required("Number of refresh cycles is required"),
	numberOfMonthsPerCycle: yup
		.number()
		.typeError("Number of months per cycle must be a number")
		.positive("Number of months per cycle must be a positive number")
		.integer("Number of months per cycle must be a whole number")
		.required("Number of months per cycle is required"),
});

const AgreementFormV2 = () => {
	const params = useParams();
	const { agreementId } = params;
	const location = useLocation();
	const isEditRoute = location.pathname.includes("/edit");
	const [file, setFile] = useState(null);
	const fileRef = useRef(null);
	const agreementForm = useSelector((state) => state.agreementForm);

	const [isLoading, setIsLoading] = useState(false);
	const [imageFile, setImageFile] = useState(null);
	const [agreementFile, setAgreementFile] = useState(null);
	const addNewContentSteps = useSelector((state) => state.addNewContentSteps);
	const validationSchema =
		addNewContentSteps?.activeStep === 1
			? stepOneValidationSchema
			: defaultValidationSchema;

	const dispatch = useDispatch();
	const user = useSelector((state) => state?.auth?.user);

	const client = generateClient();

	const formik = useFormik({
		initialValues: {
			...agreementForm,
			companyName: user?.attributes?.["custom:companyName"],
		},
		validationSchema,
		onSubmit: async (values) => {
			if (isEditRoute && agreementId) {
				await handleUpdateAgreement(values);
				return;
			}

			if (agreementForm?.id) {
				// dispatch(handleNext());
				await handleUpdateAgreement(values);
				return;
			} else {
				await handleCreateAgreement(values);
			}
		},
	});

	const handleCreateAgreement = async (values) => {
		try {
			setIsLoading(true);

			// Check if there's a new image file to upload
			if (imageFile instanceof File) {
				await uploadData({
					key: values.paymentReceiptFilePath,
					data: imageFile,
				}).result;
			}

			// Check if there's a new agreement file to upload
			if (agreementFile instanceof File) {
				await uploadData({
					key: values.agreementFilePath,
					data: agreementFile,
				}).result;
			} else if (!values.agreementFilePath) {
				// If no new file is selected and no existing file path, throw an error
				throw new Error("Agreement file is required");
			}

			const response = await client.graphql({
				query: createAgreement,
				variables: {
					input: {
						...values,
					},
				},
			});

			if (response.data.createAgreement) {
				dispatch(setAgreementDetails(response.data.createAgreement));
				dispatch(handleComplete());
				toast.success("Agreement created successfully!");
			}
		} catch (error) {
			toast.error("Error creating agreement: " + error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleUpdateAgreement = async (values) => {
		try {
			setIsLoading(true);
			if (imageFile) {
				await uploadData({
					key: values.paymentReceiptFilePath,
					data: imageFile,
				}).result;
			}
			if (agreementFile?.type) {
				await uploadData({
					key: values.agreementFilePath,
					data: agreementFile,
				}).result;
			}

			// Create a new object with only the fields that should be updated
			const updateInput = {
				id: agreementId || agreementForm?.id,
				companyName: values.companyName,
				dateOfAgreement: values.dateOfAgreement,
				paymentDueDate: values.paymentDueDate,
				currency: values.currency,
				daysOfAgreement: values.daysOfAgreement,
				licensingRegion: values.licensingRegion,
				licensingCountry: values.licensingCountry, // Assuming it should be a string
				carrierType: values.carrierType, // Assuming it should be a string
				numberOfRefreshCycles: values.numberOfRefreshCycles,
				airline: values.airline, // Assuming it should be a string
				amountPaid: parseFloat(values.amountPaid), // Convert to number if needed
				amountRemaining: values.amountRemaining
					? parseFloat(values.amountRemaining)
					: null,
				numberOfMonthsPerCycle: values.numberOfMonthsPerCycle,
				contractValue: values.contractValue,
				paymentStatus: values.paymentStatus,
				paymentPlan: values.paymentPlan,
				paymentTerm: values.paymentTerm,
				paymentReceiptFilePath: values.paymentReceiptFilePath,
				agreementFilePath: values.agreementFilePath,
			};

			const response = await client.graphql({
				query: updateAgreement,
				variables: {
					input: updateInput,
				},
			});

			if (response.data.updateAgreement) {
				dispatch(setAgreementDetails(response.data.updateAgreement));
				dispatch(handleNext());
				toast.success("Agreement updated successfully!");
			}
		} catch (error) {
			toast.error("Error updating agreement: " + error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleFileUpload = (event) => {
		const uploadedFile = event.target.files[0];
		if (uploadedFile && uploadedFile.type === "application/pdf") {
			setAgreementFile(uploadedFile);
			const guid = uuidv4();
			formik.setFieldValue(
				"agreementFilePath",
				`agreement-files/${guid}/${uploadedFile.name}`
			);
		} else {
			alert("Please upload a PDF or DOC file.");
			setFile(null);
		}
	};

	const handleSaveNext = async () => {
		const formErrors = await formik.validateForm();
		console.log(formErrors, "formErrors");
		if (Object.keys(formErrors).length > 0) {
			// Set all fields as touched if there are errors
			formik.setTouched(
				Object.keys(formErrors).reduce((acc, key) => {
					acc[key] = true;
					return acc;
				}, {})
			);
		} else {
			// If no errors, proceed with the dispatch action
			dispatch(handleComplete());
		}
	};

	const handleClearAgreementForm = () => {
		setAgreementFile(null);
		formik.resetForm();
		dispatch(resetAgreementForm());
	};

	useEffect(() => {
		formik.setFieldValue(
			"numberOfMonthsPerCycle",
			formik.values.daysOfAgreement /
				(formik.values.numberOfRefreshCycles * 30)
		);
	}, [formik.values.numberOfRefreshCycles]);

	console.log("formik", formik);

	return (
		<Box sx={{ width: "100%", p: 2 }}>
			<form onSubmit={formik.handleSubmit}>
				{addNewContentSteps?.activeStep === 1 ? (
					<CommercialTermsOfAgreement
						isLoading={isLoading}
						formik={formik}
					/>
				) : (
					// <Typography>Commercial</Typography>
					<>
						<Grid container spacing={4}>
							{/* Left Column */}
							<Grid item xs={12} md={6}>
								{/* Company Information Section */}

								<Stack spacing={3}>
									{/* <InputText
										label="Company Name"
										placeholder="Enter your company name here..."
										value={formik.values.companyName || ""}
										name="companyName"
										id="companyName"
										formik={formik}
									/> */}

									<ReadOnlyInput
										label={"Company Name"}
										value={
											user?.attributes?.[
												"custom:companyName"
											]
										}
									/>
									<InputText
										label="Signing Authority Name"
										placeholder="Enter name here..."
										value={
											formik.values
												.signingAuthorityName || ""
										}
										name="signingAuthorityName"
										id="signingAuthorityName"
										formik={formik}
									/>
									<InputText
										label="Email Address"
										placeholder="Enter email address here.."
										value={formik.values.emailAddress}
										name="emailAddress"
										id="emailAddress"
										formik={formik}
									/>
									<Box>
										<Selector
											noneValue="Select Number of Refresh Cycles"
											label="Number Of Refresh Cycles"
											options={[
												{ label: "1", value: 1 },
												{ label: "2", value: 2 },
												{ label: "3", value: 3 },
												{ label: "6", value: 6 },
												{ label: "9", value: 9 },
											]}
											formik={formik}
											name="numberOfRefreshCycles"
										/>
									</Box>
									<LocalizationProvider
										dateAdapter={AdapterDayjs}
									>
										<Box>
											<Typography sx={{ mb: 1 }}>
												Date Of Agreement Signing{" "}
												<span
													style={{
														color: "red",
														marginLeft: "5px",
													}}
												>
													*
												</span>
											</Typography>
											<DatePicker
												value={
													formik.values
														.dateOfAgreement
														? dayjs(
																formik.values
																	.dateOfAgreement
														  )
														: null
												}
												sx={{ width: "100%" }}
												name="dateOfAgreement"
												onChange={(newValue) => {
													const isoDate = newValue
														? dayjs(
																newValue
														  ).toISOString()
														: "";
													formik.setFieldValue(
														"dateOfAgreement",
														isoDate
													);
												}}
												maxDate={dayjs()}
											/>
											{formik?.touched?.dateOfAgreement &&
												formik?.errors
													?.dateOfAgreement && (
													<Typography
														sx={{ color: "red" }}
													>
														{
															formik.errors
																?.dateOfAgreement
														}
													</Typography>
												)}
										</Box>
									</LocalizationProvider>
									<Stack gap={2}>
										<Typography variant="h6" sx={{ mb: 3 }}>
											Technical Point of Contact
										</Typography>
										<InputText
											label={"Name :"}
											id={"technicalName"}
											placeholder={"Enter name here..."}
											formik={formik}
											value={
												formik?.values?.technicalName
											}
											type={"text"}
											name={"technicalName"}
											flexDirection="row"
											alignItems="center"
											marginLeft="10px"
											labelStyle={{ minWidth: 110 }}
										/>
										<InputText
											label={"Designation :"}
											id={"technicalDesignation"}
											placeholder={"Enter name here..."}
											formik={formik}
											value={
												formik?.values
													?.technicalDesignation
											}
											type={"text"}
											name={"technicalDesignation"}
											flexDirection="row"
											alignItems="center"
											marginLeft="10px"
											labelStyle={{ minWidth: 110 }}
										/>
										<InputText
											label={"Email :"}
											id={"technicalEmail"}
											placeholder={"Enter name here..."}
											formik={formik}
											value={
												formik?.values?.technicalEmail
											}
											type={"text"}
											name={"technicalEmail"}
											flexDirection="row"
											alignItems="center"
											marginLeft="10px"
											labelStyle={{ minWidth: 110 }}
										/>
										<PhoneNumberInput
											formik={formik}
											name={"technicalPhoneNumber"}
											containerStyle={{
												display: "flex",
												alignItems: "center",
											}}
											labelStyle={{ minWidth: 110 }}
										/>
									</Stack>
									<Stack gap={2}>
										<Typography variant="h6" sx={{ mb: 3 }}>
											Commercial Point Of Contact
										</Typography>
										<InputText
											label={"Name :"}
											id={"commercialName"}
											placeholder={"Enter name here..."}
											formik={formik}
											value={
												formik?.values?.commercialName
											}
											type={"text"}
											name={"commercialName"}
											flexDirection="row"
											alignItems="center"
											marginLeft="10px"
											labelStyle={{ minWidth: 110 }}
										/>
										<InputText
											label={"Designation :"}
											id={"commercialDesignation"}
											placeholder={"Enter name here..."}
											formik={formik}
											value={
												formik?.values
													?.commercialDesignation
											}
											type={"text"}
											name={"commercialDesignation"}
											labelStyle={{ minWidth: 110 }}
											flexDirection="row"
											alignItems="center"
											marginLeft="10px"
										/>
										<InputText
											label={"Email :"}
											id={"commercialEmail"}
											placeholder={"Enter name here..."}
											formik={formik}
											value={
												formik?.values?.commercialEmail
											}
											type={"text"}
											name={"commercialEmail"}
											flexDirection="row"
											alignItems="center"
											marginLeft="10px"
											labelStyle={{ minWidth: 110 }}
										/>
										<PhoneNumberInput
											formik={formik}
											name={"commercialPhoneNumber"}
											containerStyle={{
												display: "flex",
												alignItems: "center",
											}}
											labelStyle={{ minWidth: 110 }}
										/>
									</Stack>
									<Typography
										component={"span"}
										variant="h6"
										sx={{ mb: 3 }}
									>
										Contract:
										<Typography
											component={"span"}
											sx={{ ml: 2 }}
										>
											(Contract should be in pdf format)
										</Typography>
									</Typography>
									<Button
										variant="contained"
										component="label"
										startIcon={<UploadFileIcon />}
										sx={{ textTransform: "capitalize" }}
										onChange={handleFileUpload}
									>
										Click To Upload Signed Contract
										<input
											type="file"
											accept=".pdf,.doc,.docx"
											hidden
										/>
									</Button>
									<Box>
										{agreementFile && (
											<Paper
												sx={{
													padding: "10px 16px",
													border: "1px solid lightgrey",
													width: "fit-content",
												}}
												elevation={2}
											>
												<Box display={"flex"}>
													<Box
														display="flex"
														gap={2}
														alignItems="center"
													>
														<DescriptionOutlinedIcon />
														<Typography
															onClick={() => {
																window.open(
																	agreementFile?.url ||
																		URL.createObjectURL(
																			agreementFile
																		),
																	"_blank"
																);
															}}
															sx={{
																cursor: "pointer",
																"&:hover": {
																	textDecoration:
																		"underline",
																},
															}}
														>
															{agreementFile.name}
														</Typography>
													</Box>
													<Button>
														<DeleteOutlineOutlined />
													</Button>
												</Box>
											</Paper>
										)}
									</Box>
								</Stack>
							</Grid>

							{/* Right Column */}
							<Grid item xs={12} md={6}>
								<Stack spacing={3}>
									<InputText
										label={"Official Address"}
										placeholder={
											"Enter your official address here.."
										}
										formik={formik}
										value={formik?.values?.officialAddress}
										id={"officialAddress"}
										name={"officialAddress"}
									/>
									<InputText
										label={"Designation"}
										placeholder={
											"Enter your designation here..."
										}
										formik={formik}
										value={formik?.values?.designation}
										id={"designation"}
										name={"designation"}
									/>
									<LicensingTerm formik={formik} />
									<InputText
										type="number"
										formik={formik}
										name="numberOfMonthsPerCycle"
										label="Number of months per cycle"
										id="numberOfMonthsPerCycle"
										value={
											formik.values.numberOfMonthsPerCycle
										}
										placeholder="Enter the number of months per cycle"
									/>
									<Box sx={{ visibility: "hidden" }}>
										<InputText
											type="number"
											formik={formik}
											name="numberOfMonthsPerCycle"
											label="Number of months per cycle"
											id="numberOfMonthsPerCycle"
											value={
												formik.values
													.numberOfMonthsPerCycle
											}
											placeholder="Enter the number of months per cycle"
										/>
									</Box>
									<Stack gap={2}>
										<Typography variant="h6" sx={{ mb: 3 }}>
											Billing Point Of Contact
										</Typography>
										<InputText
											label={"Name :"}
											id={"billingName"}
											placeholder={"Enter name here..."}
											formik={formik}
											value={formik?.values?.billingName}
											type={"text"}
											name={"billingName"}
											flexDirection="row"
											alignItems="center"
											marginLeft="10px"
											labelStyle={{ minWidth: 110 }}
										/>
										<InputText
											label={"Designation :"}
											id={"billingDesignation"}
											placeholder={"Enter name here..."}
											formik={formik}
											value={
												formik?.values
													?.billingDesignation
											}
											type={"text"}
											name={"billingDesignation"}
											flexDirection="row"
											alignItems="center"
											marginLeft="10px"
											labelStyle={{ minWidth: 110 }}
										/>
										<InputText
											label={"Email :"}
											id={"billingEmail"}
											placeholder={"Enter name here..."}
											formik={formik}
											value={formik?.values?.billingEmail}
											type={"text"}
											name={"billingEmail"}
											flexDirection="row"
											alignItems="center"
											marginLeft="10px"
											labelStyle={{ minWidth: 110 }}
										/>

										<PhoneNumberInput
											formik={formik}
											name="billingPhoneNumber"
											containerStyle={{
												display: "flex",
												alignItems: "center",
											}}
										/>
									</Stack>
									{/* Payment Information Section */}
								</Stack>
							</Grid>
						</Grid>
						{formik?.values?.agreementFilePath && (
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									mt: 3,
								}}
							>
								<Button
									variant="contained"
									color="error"
									onClick={handleClearAgreementForm}
								>
									Clear
								</Button>

								<Button
									variant="contained"
									sx={{
										backgroundColor: "primary.darker",
										color: "secondary.main",
									}}
									onClick={handleSaveNext}
								>
									Save & Next
								</Button>
							</Box>
						)}
					</>
				)}

				{/* Upload Agreement Section */}

				{/* Submit Button */}
			</form>
		</Box>
	);
};

export default AgreementFormV2;
